import { message, Popover } from 'antd';
import React, { useContext, useEffect, useState } from 'react';

import Button from '@/components/common/Button';
import { RightContext } from '@/components/RightProvider/context';
import { useTranslate } from '@/hooks/useTranslate';
import { cancelAccountDeletion, checkLogin } from '@/services/login';
import { stripeSubscribePolling } from '@/services/order';
import useLoginStore from '@/store/Login';
import UserStore from '@/store/User';
import { formatDate, getCookie } from '@/utils';
import { AIFunction, FC_Feature } from '@/utils/const';
import { isCnArea } from '@/utils/envHelper';
import { EventNewID, SubscribeReasonEnum } from '@/utils/eventlog/eventNewConfig';
import { pushGTMEvent } from '@/utils/eventlog/gtmDataLayer';
import { trackEvent } from '@/utils/eventlog/logAdapter';
import { t } from '@/utils/helper/intl';

import CommonModal, {
  AICreditModal,
  ExchangeCodeModal,
  PaymentModal,
} from '../CommonModal';
import { useChangePasswordModal } from '../CommonModal/changePassword';
import { getUserTitle } from '../CommonModal/payment/utils';
import CreditIcon from '../UserCredit/icon/credit';
import UpgradeIcon from '../UserCredit/icon/upgrade';
import { MembershipLink, PrivacyLink, TermsLink } from './const';
import ArrowIcon from './icon/arrow';
import CalendarIcon from './icon/calendar';
import CheckBoxIcon from './icon/CheckBoxUnSelected';
import CopyUID from './icon/copy';
import DiamondIcon from './icon/diamond';
import { UserIcon } from './icon/user';
import styles from './styles.module.scss';

interface FeatureContent {
  label: string;
  event: () => void;
  isPolicy?: boolean;
  popoverContent?: any;
  isMultipleOption?: boolean;
}

const timestampToYMD = (timestamp: number) => {
  const date = new Date(timestamp);
  const year = date.getUTCFullYear();
  const month = ('0' + (date.getUTCMonth() + 1)).slice(-2);
  const day = ('0' + date.getUTCDate()).slice(-2);
  return year + '.' + month + '.' + day;
};

export const AvatarIcon = () => {
  const currentVip = UserStore((store) => store.currentVip);
  const isFreeVip = currentVip.key === 'freeVip';

  return (
    <div
      className={`relative ${!isFreeVip ? 'border-[1px] border-[#e6b63e] rounded-full' : ''}`}
    >
      <UserIcon />
      {!isFreeVip && (
        <div className="absolute right-[-5px] bottom-[-5px]">
          <DiamondIcon />
        </div>
      )}
    </div>
  );
};

const UpgradeBtn = ({ onClick }: { onClick: () => void }) => {
  const { t } = useTranslate();
  const currentVip = UserStore((store) => store.currentVip);
  if (
    !currentVip.isRequestEnd ||
    currentVip.key === 'enterpriseVip' ||
    currentVip.key === 'advancedVip'
  ) {
    return;
  }
  return (
    <div className="w-[106px] flex flex-col justify-end items-center gap-[8px]">
      <Button
        style={{
          background:
            'linear-gradient(90deg, rgb(77, 47, 255) 0%, rgb(239, 92, 255) 100%)',
        }}
        className="!text-[14px] !font-[500] !leading-[20px] w-full flex items-center gap-[6px]"
        onClick={onClick}
      >
        <UpgradeIcon />
        <span> {t('Upgrade')}</span>
      </Button>
    </div>
  );
};
const BenefitDisplay = () => {
  const currentVip = UserStore((store) => store.currentVip);
  return (
    <div className="mt-[32px] w-full bg-common-popover-user-benefit-bg rounded-[6px] relative flex flex-col justify-center items-center py-[4px] gap-[4px]">
      <Popover
        classNames={{
          root: styles['user-expire'],
        }}
        align={{
          offset: [0, -10],
        }}
        destroyOnHidden
        placement="top"
        content={t('merror.sub.expire') + ' ' + timestampToYMD(currentVip.endTime)}
      >
        <div className="w-[16px] h-[16px] absolute top-[8px] left-[8px]">
          <CalendarIcon />
        </div>
      </Popover>

      <p className="text-[16px] font-[500] leading-[22px] text-[#FDD014]">
        {t(getUserTitle(currentVip.key))}
      </p>
      <p className="flex items-center font-number text-[14px] font-[600] leading-[20px] text-common-text-base-color tracking-[0.5px]">
        <span className="relative flex">
          <CreditIcon className="absolute -left-[18px]" />
          <span>{currentVip.balance}</span>
        </span>
      </p>
    </div>
  );
};
const GetCreditButton = ({ onClick }: { onClick: () => void }) => {
  const { t } = useTranslate();

  return (
    <div
      style={{
        width: 106,
        display: 'inline-block',
        borderRadius: 8,
        padding: 2,
        background: 'linear-gradient(90deg, #4D2FFF, #EF5CFF)',
        boxSizing: 'border-box',
        cursor: 'pointer',
        height: 38,
      }}
      onClick={onClick}
    >
      <div
        style={{
          textAlign: 'center',
          borderRadius: 6,
          background: 'var(--app-bg-card)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: ' 4px 10px',
          height: '100%',
          fontWeight: 600,
          fontSize: 12,
        }}
      >
        <CreditIcon />
        <span
          style={{
            backgroundImage: 'linear-gradient(90deg, #4D2FFF, #EF5CFF)',
            WebkitBackgroundClip: 'text',
            backgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            color: 'transparent',
          }}
        >
          {t('Buy-More-AICredits')}
        </span>
      </div>
    </div>
  );
};

const CancelUserContent = ({
  closeModal,
  t,
}: {
  closeModal: () => void;
  t: (key: string) => string;
}) => {
  const [agreeState, setAgreeState] = useState(false);
  const [confirmState, setConfirmState] = useState(false);
  const currentVip = UserStore((state) => state.currentVip);

  const { accountId, accountType } = getCookie('user') || {};
  const exceptCancelTime = currentVip.exceptCancelTime;
  const isInCancellation = formatDate(exceptCancelTime);

  const handleWithdrawCancellation = async () => {
    try {
      const res = await cancelAccountDeletion({ accountId, accountType });
      if (res?.success || res?.code === 200) {
        message.success(t('fc.f93929a8'));
        // 刷新用户信息
        await UserStore.getState().getUserBenefit(false);
        closeModal();
      } else {
        message.error(res?.message);
      }
    } catch (error) {
      console.error('Cancel account deletion error:', error);
    }
  };

  return (
    <>
      <div className="font-[600] text-[16px] mb-[8px]">{t('fc.dc9bbdfa')}</div>
      <div className="text-[14px] leading-[22px] text-[#cf5ecf] underline">
        {t('fc.e1a200f9')}
      </div>
      <div className="text-[14px] leading-[22px] mb-[20px]">{t('fc.f28bb21a')}</div>
      <div className="mt-[20px] text-app-primary">
        <div className="font-[600] text-[16px] mb-[8px]">{t('fc.bb450cd7')}</div>
        <ul className="list-decimal pl-[20px] text-[14px] leading-[22px] space-y-[8px]">
          <li>{t('fc.4f42fd30')}</li>
          <li>{t('fc.3e2496a1')}</li>
          <li>{t('fc.cf8513fd')}</li>
          <li>{t('fc.7bf8d65d')}</li>
          <li>{t('fc.f71e23e7')}</li>
          <li>{t('fc.1fb9717d')}</li>
        </ul>
      </div>
      <div className="mt-[16px] text-[14px] text-app-primary font-bold">
        {t('fc.c21fd01b')}
      </div>
      {exceptCancelTime ? (
        <div className="mt-[20px] text-center text-[14px] font-[500] text-[#FF4D4F]">
          {`${t('fc.16e5b64e')} ${isInCancellation}`}
        </div>
      ) : (
        <div className="flex flex-col">
          <div
            className="cursor-pointer flex items-center mt-[20px]"
            onClick={() => setAgreeState(!agreeState)}
          >
            <div className=" mr-1">
              <CheckBoxIcon checked={agreeState}></CheckBoxIcon>
            </div>
            <span>{t('fc.19701b86')}</span>
          </div>
          <div
            className="cursor-pointer flex items-center"
            onClick={() => setConfirmState(!confirmState)}
          >
            <div className=" mr-1">
              <CheckBoxIcon checked={confirmState}></CheckBoxIcon>
            </div>
            <span>{t('fc.b9c33fac')}</span>
          </div>
        </div>
      )}
      <div className="flex flex-row items-center justify-center gap-[10px] mt-[30px]">
        {!exceptCancelTime ? (
          <>
            <Button
              style={{
                border: '1px solid var(--app-border)',
                color: 'var(--app-text-primary)',
                background: 'var(--app-bg-card)',
              }}
              className="!font-normal whitespace-nowrap !px-[40px] !py-[6px] !rounded-[6px] min-w-[142px]"
              onClick={closeModal}
            >
              {t('merror.operate.cancel')}
            </Button>
            <Button
              disabled={!agreeState || !confirmState}
              className="!font-normal whitespace-nowrap !px-[40px] !py-[6px] !rounded-[6px] min-w-[142px]"
              onClick={() => {
                useLoginStore
                  .getState()
                  .justCancelUser()
                  .then(() => {
                    closeModal();
                  });
              }}
            >
              {t('f7b3e8b1c849e3a938e277a5a5d5e5e5')}
            </Button>
          </>
        ) : (
          <Button
            style={{
              background:
                'linear-gradient(90deg, rgb(77, 47, 255) 0%, rgb(239, 92, 255) 100%)',
            }}
            className="!font-normal whitespace-nowrap !px-[40px] !py-[6px] !rounded-[6px] min-w-[240px]"
            onClick={handleWithdrawCancellation}
          >
            {t('fc.23f5b135')}
          </Button>
        )}
      </div>
    </>
  );
};

const PopoverContainer = ({ onClose }: { onClose: () => void }) => {
  const rightContext = useContext(RightContext);
  const currentVip = rightContext.currentVip;
  const { t } = useTranslate();
  const [userInfo] = useState({
    name: getCookie('user')?.email || getCookie('user')?.accountId || '-',
    uid: getCookie('user').uid || '-',
  });
  const logout = useLoginStore((store) => store.logout);
  const userSalt = UserStore((store) => store.userSalt);
  const setUserSalt = UserStore((store) => store.setUserSalt);
  const [feature, setFeature] = useState<Array<FeatureContent>>([]);
  const { showModal: showChangePasswordModal } = useChangePasswordModal();

  const onBuyAICredit = () => {
    onClose();
    const currentFeature =
      (sessionStorage.getItem(FC_Feature) as AIFunction) || AIFunction.Other;
    pushGTMEvent('view_pricing_plans', {
      trigger_type: 'nav_menu',
      nav_menu_item: 'user_account_panel',
    });
    AICreditModal.show({
      feature: currentFeature,
      reason: SubscribeReasonEnum.Me,
    });
  };
  const onBuyCommodity = () => {
    onClose();
    const currentFeature =
      (sessionStorage.getItem(FC_Feature) as AIFunction) || AIFunction.Other;
    pushGTMEvent('view_pricing_plans', {
      trigger_type: 'nav_menu',
      nav_menu_item: 'user_account_panel',
    });
    PaymentModal.show({
      feature: currentFeature,
      reason: SubscribeReasonEnum.Me,
    });
  };

  useEffect(() => {
    const fetchFeatureData = async () => {
      const featureData = [
        {
          label: t('merror.code.meredeem'),
          event: () => {
            ExchangeCodeModal.show({
              from: 'user',
            });
          },
        },
        // {
        //   label: t('merror.login.help'),
        //   event: () => {
        //     window.open(
        //       isCnArea
        //         ? 'https://quvideo.feishu.cn/docx/Nf4Td8jdWooZjLxxEtEcw1CqnQb'
        //         : isFWebSite
        //           ? 'https://quvideo.feishu.cn/wiki/MqU9wtrEji9ZMykz2AXcKxBsnld'
        //           : 'https://quvideo.feishu.cn/wiki/TZF9wIljWiDHEgkj0vdciuJ5ncd',
        //       '_blank',
        //     );
        //   },
        // },
        {
          label: t('Agreement'),
          event: () => {},
          isPolicy: true,
          isMultipleOption: true,
          popoverContent: () => {
            const menu = [
              {
                key: '1',
                label: t('Terms of Services'),
                link: TermsLink,
              },
              {
                key: '2',
                label: t('Privacy Policy'),
                link: PrivacyLink,
              },
              {
                key: '3',
                label: t('Membership Policy'),
                link: MembershipLink,
              },
            ];
            return (
              <div className={styles['popover-menu']}>
                {menu.map((item) => {
                  return (
                    <div key={item.key}>
                      <span>
                        <a href={item.link} target="_blank">
                          {item.label}
                        </a>
                      </span>
                    </div>
                  );
                })}
              </div>
            );
          },
        },
      ];

      // if (currentVip.weight === 3) {
      //   featureData.unshift({
      //     label: t('Buy-More-AICredits'),
      //     event: () => {
      //       AICreditModal.show({ showBanner: false, feature: 'me' });
      //     },
      //   });
      // }

      const addChangePasswordFeature = (salt: string | null) => {
        if (salt) {
          featureData.push({
            label: t('Change-Password'),
            event: () => {
              showChangePasswordModal({ hasPwd: true });
            },
          });
        }
      };
      const addSetPasswordFeature = () => {
        featureData.push({
          label: t('Set-Password'),
          event: () => {
            showChangePasswordModal({ hasPwd: false });
          },
        });
      };

      // 注销账号
      featureData.push({
        label: t('merror.login.cancelUser'),
        event: () => {
          CommonModal.show((t) => ({
            width: 800,
            title: t('b1c2d3e4f5a6b7c8d9e0f1a2b3c4d5e6'),
            content: (closeModal) => {
              return <CancelUserContent closeModal={closeModal} t={t} />;
            },
            customPadding: {
              x: 58,
              y: 40,
            },
            footer: false,
          }));
        },
      });

      if (!isCnArea) {
        // 订阅管理
        featureData.push({
          label: t('Subscription Management'),
          event: async () => {
            const res = await stripeSubscribePolling();
            if (res?.data?.redirectUrl) {
              window.open(res.data.redirectUrl, '_blank', 'noopener=yes,noreferrer=yes');
            } else {
              window.open(
                'https://billing.stripe.com/p/login/28o3e8gcH4kh2nmcMM',
                '_blank',
              );
            }
          },
        });
      }

      // 修改密码

      if (userSalt) {
        addChangePasswordFeature(userSalt);
      } else {
        if (isCnArea) {
          // 不管是邮箱登录还是手机号登录，一律按salt有没有判定
          const pwdLoginRes = await checkLogin({
            accountType: 65,
            accountId: getCookie('user').accountId,
          });
          if (pwdLoginRes?.data?.salt) {
            // 设置过密码
            setUserSalt(pwdLoginRes?.data?.salt);
            addChangePasswordFeature(pwdLoginRes?.data?.salt);
          } else {
            // 没有设置过密码
            addSetPasswordFeature();
            setUserSalt('');
          }
        } else {
          const res = await checkLogin({
            accountType: 65,
            accountId: getCookie('user').accountId,
          });
          if (res?.data?.salt) {
            // 普通邮箱登录，有salt，执行修改密码而不是设置密码
            setUserSalt(res?.data?.salt);
            addChangePasswordFeature(res?.data?.salt);
          } else {
            const hasEmail = getCookie('user').email;
            // 谷歌登录
            if (!hasEmail && res?.data?.state === 1) {
              addSetPasswordFeature();
              useLoginStore.getState().setIsNewUser(true);
            }
            setUserSalt('');
          }
        }
      }

      setFeature(featureData);
    };
    fetchFeatureData();
  }, [userSalt]);

  return (
    <div
      className="flex p-[10px] flex-col"
      style={{ maxHeight: '80vh', overflowY: 'auto' }}
    >
      {/* 头像 和id */}
      <div>
        <div className="pt-[12px] flex flex-col gap-[14px]">
          <div className="flex flex-col ml-[16px]">
            <p className="text-app-primary font-[500] text-[14px] leading-[16px] tracking-[0.5px] flex">
              ID: {userInfo.uid}
              <span
                className="pl-[8px]"
                onClick={() => {
                  navigator.clipboard.writeText(userInfo.uid);
                  message.success(t('merror.operate.copied'));
                }}
              >
                <CopyUID className="cursor-pointer" />
              </span>
            </p>
            <p className="text-app-tertiary font-[400] text-[12px] leading-[16px] max-w-[220px] tracking-[0.5px] truncate">
              {userInfo.name}
            </p>
          </div>
        </div>
      </div>
      <div className="px-[16px] py-[12px] bg-app-card border border-app rounded-[8px] flex flex-col gap-[16px] mt-[10px]">
        <div className="flex justify-between items-center">
          <div className="flex flex-col gap-[4px]">
            <div className="text-app-primary text-[16px] flex items-center gap-[4px] font-[500]">
              <span>{t(getUserTitle(currentVip.key))}</span>
              {currentVip.key !== 'freeVip' && <DiamondIcon />}
            </div>
            {currentVip.isExpireUser && (
              <div className="text-app-tertiary text-[10px] h-[16px]">{t('Expired')}</div>
            )}
          </div>
          <UpgradeBtn onClick={onBuyCommodity} />
        </div>

        <div className="flex justify-between">
          <div className="flex flex-col gap-[4px]">
            <div className="text-app-primary text-[16px] flex items-center gap-[4px]">
              <CreditIcon />
              <span className="text-[#fff] font-[500]">{currentVip.balance}</span>
            </div>
            {currentVip.key !== 'freeVip' && (
              <div className="text-app-tertiary text-[10px]">
                {t('merror.sub.expire') + ' ' + timestampToYMD(currentVip.endTime)}
              </div>
            )}
          </div>
          {currentVip.key == 'freeVip' ? (
            <span className="text-app-secondary pr-[20px]">
              {t('95ed516772d48f291fb73a2a10d258b1')}
            </span>
          ) : (
            <GetCreditButton onClick={onBuyAICredit} />
          )}
        </div>
      </div>
      {/* 操作 */}
      <div
        className="flex flex-col gap-[8px] mt-[10px] border-y-[1px] text-app-primary border-app py-[10px]"
        onClick={() => {
          onClose();
        }}
      >
        {feature.map((item, index) => {
          const content = (
            <div
              key={index}
              className="text-[15px] leading-[20px] font-[400] tracking-[0.24px] flex justify-between items-center px-[16px] py-[8px] pr-[4px] cursor-pointer rounded-[6px] hover:bg-app-hover transition-colors"
              onClick={item.event}
            >
              <span className="font-sans">{item.label}</span>
              {item.isMultipleOption && (
                <span>
                  <ArrowIcon />
                </span>
              )}
            </div>
          );

          return item.isPolicy ? (
            <Popover
              key={index}
              align={{
                offset: [14, 0],
              }}
              destroyOnHidden
              classNames={{
                root: styles['more-operate-popover'],
              }}
              arrow={false}
              content={item.popoverContent}
              placement="rightTop"
            >
              {content}
            </Popover>
          ) : (
            content
          );
        })}
      </div>
      {/* 退出 */}
      <div
        className="flex gap-[16px] py-[4px] pl-[16px] mt-[8px] cursor-pointer rounded-[6px] hover:bg-app-hover transition-colors"
        onClick={() => {
          onClose();
          CommonModal.show((t) => ({
            width: 800,
            title: t('merror.menu.logoutTitle'),
            content: () => (
              <p className="text-center text-[18px] font-medium leading-[24px] tracking-[0.36px]">
                {t('merror.menu.logoutText')}
              </p>
            ),
            cancelText: t('merror.operate.cancel'),
            confirmText: t('merror.operate.ok'),
            customPadding: {
              x: 58,
              y: 40,
            },
            onConfirm: async (close) => {
              logout();
              // window.location.href = window.location.origin + '/login';
            },
          }));
        }}
      >
        <span className="text-[15px] text-app-primary  tracking-[0.24px] font-[400]">
          {t('merror.menu.logoutTitle')}
        </span>
      </div>
    </div>
  );
};

const UserAccount: React.FC = () => {
  const rightContext = useContext(RightContext);
  const [open, setOpen] = useState(false);

  const handleClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (open) return;
    // 先同步检查登录状态
    if (rightContext.getIsLoggedIn()) {
      // 已登录，直接打开 Popover
      setOpen(true);
      trackEvent(EventNewID.ACCOUNT_CLICK);
      return;
    }

    // 未登录，调用登录弹窗
    pushGTMEvent('login_popup', {
      trigger_type: 'nav_menu',
      nav_menu_item: 'user_account_icon',
    });
    await rightContext.getUserLoginState({
      skipGTMTracking: true,
      onReady: () => {
        trackEvent(EventNewID.ACCOUNT_CLICK);
        setOpen(true);
      },
    });
  };

  return (
    <Popover
      classNames={{
        root: styles['user-account'],
      }}
      align={{
        offset: [10, 10],
      }}
      arrow={false}
      destroyOnHidden
      content={<PopoverContainer onClose={() => setOpen(false)} />}
      trigger="click"
      placement="bottomRight"
      open={open}
      onOpenChange={(visible) => {
        // 只允许关闭，不允许通过 Popover 的默认行为打开
        if (!visible) {
          setOpen(false);
        }
      }}
    >
      <div
        className="cursor-pointer relative "
        onClick={handleClick}
        element-name="user_account_icon"
      >
        <AvatarIcon />
      </div>
    </Popover>
  );
};

export default UserAccount;
